import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDeactivateApiKey } from "@/features/tokens/api/token.queries";
import type { IApiKey } from "@/features/tokens/types/token.types";
import {
  createMonitoringIcon,
  getMonitoringTypeFromToken,
  MONITORING_BADGE_STYLES,
  MONITORING_TYPES,
  type MonitoringType,
} from "@/constants/monitoring";
import { Pagination } from "@/components/Common/Pagination";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";
import TokenSkeleton from "../TokenSkeleton";
import { useGetApiToken } from "@/features/tokens/api/token.queries";
import { Spinner } from "@/components/ui/spinner";

function getMonitoringBadgeClassName(type: MonitoringType): string {
  const baseClasses =
    "flex h-[28px] w-[28px] items-center justify-center rounded-[6px] p-[3px] font-light";
  const colorClass = MONITORING_BADGE_STYLES[type] || "bg-gray-500";

  return `${baseClasses} ${colorClass}`;
}

function getUsageColor(
  isPremium: boolean,
  monitoringType: MonitoringType,
): string {
  if (isPremium) {
    return "bg-yellow-500"; // Premium tokens get yellow usage bar
  }
  return MONITORING_BADGE_STYLES[monitoringType] || "bg-gray-500";
}

export default function Token() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [activeOnly, setActiveOnly] = useState(true);
  const { data, isLoading, refetch, isFetching } = useGetApiToken(
    currentPage,
    pageSize,
    activeOnly,
  );
  const deleteTokenMutation = useDeactivateApiKey();
  const showValues = true;
  const showPercentages = true;

  const getEntityTypeFromToken = (token: string): string => {
    if (!token) return "Unknown";
    if (token.startsWith("premium")) return "Premium";
    if (token.startsWith("qm")) return "Source Monitoring";
    if (token.startsWith("inf")) return "Influencer Monitoring";
    if (token.startsWith("ps")) return "Post Monitoring";
    if (token.startsWith("sm")) return "Keyword Monitoring";
    if (token.startsWith("enterprise")) return "Keyword Monitoring";
    if (token.startsWith("pulse")) return "Pulse";
    return "Unknown";
  };

  const handleAddToken = () => {
    navigate({ to: "/manager/subscription-management/add-new-token" });
  };

  const handleDeleteToken = (tokenId: string) => {
    deleteTokenMutation.mutate(tokenId);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleActiveToggle = () => {
    setActiveOnly(!activeOnly);
    setCurrentPage(1); // Reset to first page when toggling
  };

  if (isLoading || isFetching) {
    return <TokenSkeleton />;
  }

  const tokens = data?.api_keys || [];
  const pagination = data?.pagination;

  return (
    <div className="flex w-full flex-col items-start justify-between space-y-5 rounded-lg bg-white p-5">
      <div className="flex w-full flex-row justify-between">
        <div className="w-full space-y-2">
          <div className="text-lg leading-5 font-bold -tracking-wide text-neutral-950">
            Tokens
          </div>
          <div className="text-black-500 text-xs leading-3">
            View your quota per token
          </div>
        </div>
        <div className="flex flex-row gap-2">
          <Button
            className={`gap-2.5 p-1.5 text-xs leading-normal font-bold tracking-normal ${
              activeOnly
                ? "bg-[#00ff4c]/10 text-[#00da41] hover:bg-[#00ff4c]/20"
                : "bg-red-500/10 text-red-500 hover:bg-red-500/20"
            }`}
            variant="minimal"
            icon
            onClick={handleActiveToggle}
          >
            <i
              className={`text-md font-light ${activeOnly ? "ri-checkbox-circle-line" : "ri-close-circle-line"}`}
            ></i>
            {activeOnly ? "Active Only" : "Show All"}
          </Button>
          <Button
            className="text-muted hover:text-muted flex flex-row items-center gap-1.5 p-1.5 text-xs leading-normal font-bold tracking-normal hover:bg-white/30"
            variant="minimal"
            onClick={handleAddToken}
          >
            <i className="ri-add-line text-md pb-0.5 font-light"></i>
            Create New Token
          </Button>
          <Button
            className="text-muted hover:text-muted gap-2 p-1.5 text-xs leading-normal font-bold tracking-normal hover:bg-white/30"
            variant="minimal"
            icon
            onClick={() => {
              refetch().then(() => {
                toast.success("Tokens refreshed successfully");
              });
            }}
            disabled={isLoading || isFetching}
          >
            {isFetching ? (
              <Spinner size="sm" className="stroke-primary!" />
            ) : (
              <>
                {" "}
                <i className="ri-loop-right-line text-sm font-light"></i>
                Refresh
              </>
            )}
          </Button>
        </div>
      </div>
      <div className="flex w-full flex-col gap-2">
        {tokens.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-gray-500">
            <div className="text-center">
              <i className="ri-key-line mb-2 text-2xl"></i>
              <p className="text-sm">No API tokens yet</p>
              <p className="text-xs text-gray-400">
                Create your first token to get started
              </p>
            </div>
          </div>
        ) : (
          <div className="scrollbar-thin overflow-x-auto">
            <table className="w-full table-fixed border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs font-medium tracking-wide text-neutral-500 uppercase">
                  <th className="w-[30%] px-4 py-3 font-medium">Token</th>
                  <th className="w-[12%] px-4 py-3 font-medium">
                    Quota
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <i className="ri-information-line ml-1 text-sm font-light"></i>
                      </TooltipTrigger>
                      <TooltipContent>
                        Quota is the quota that has been allocated to the user.
                      </TooltipContent>
                    </Tooltip>
                  </th>
                  <th className="w-[8%] px-4 py-3 font-medium">
                    CPM
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <i className="ri-information-line ml-1 text-sm font-light"></i>
                      </TooltipTrigger>
                      <TooltipContent>
                        CPM is the calls per minute that has been allocated to
                        the user.
                      </TooltipContent>
                    </Tooltip>
                  </th>
                  <th className="w-[20%] px-4 py-3 font-medium">Usage</th>
                  <th className="w-[10%] px-4 py-3 font-medium">Status</th>
                  <th className="w-[11%] px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((token: IApiKey, index: number) => {
                  const tokenType = getEntityTypeFromToken(
                    token.api_key_preview,
                  );
                  const usagePercentage = Math.min(
                    ((token.usage_count || 0) / token.max_usage) * 100,
                    100,
                  );

                  // Determine if it's a premium token
                  const isPremium = token.api_key_preview.startsWith("premium");

                  // Get monitoring type for non-premium tokens
                  const monitoringType: MonitoringType = isPremium
                    ? MONITORING_TYPES.KEYWORD // fallback for premium
                    : getMonitoringTypeFromToken(token.api_key_preview);

                  const monitoringIcon = createMonitoringIcon(
                    monitoringType,
                    "text-md font-light  text-white ",
                    true,
                  );

                  return (
                    <tr
                      key={`row-${token.api_key_id}-${index}`}
                      className="bg-black-900/50 hover:bg-black-900/80 transition-colors"
                    >
                      <td className="px-4 py-4 font-bold text-neutral-950">
                        <div className="flex min-w-0 items-center gap-2">
                          {isPremium ? (
                            <div className="flex h-[28px] w-[38px] items-center justify-center rounded-[8px] bg-yellow-500 p-[4px]">
                              <i className="ri-star-line text-sm font-light text-white"></i>
                            </div>
                          ) : (
                            <div
                              className={`${getMonitoringBadgeClassName(
                                monitoringType,
                              )} flex-shrink-0`}
                            >
                              {monitoringIcon}
                            </div>
                          )}
                          <span
                            className="text-md truncate leading-4 font-bold -tracking-wide text-neutral-950"
                            title={token.api_key_preview}
                          >
                            {token.api_key_preview}
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-4 text-neutral-950">
                        {token.billing ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <p className="text-xs">
                                Tier:{" "}
                                {token.billing.charge_breakdown?.[0]?.tier ??
                                  "N/A"}
                              </p>
                            </TooltipTrigger>
                            <TooltipContent>
                              Range of the tier that the token is in. Reaching
                              the max of the tier will upgrade to the next tier.
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <p>{token.quota_remaining.toLocaleString()}</p>
                        )}
                      </td>

                      <td className="px-4 py-4 text-neutral-950">
                        {token.calls_per_min}
                      </td>

                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          <div className="text-xs text-neutral-950">
                            {showValues && (
                              <span>
                                {token.billing?.charge_breakdown?.[0] != null
                                  ? (
                                      (
                                        token.billing.charge_breakdown[0] as {
                                          count?: number;
                                        }
                                      )?.count ??
                                      token.usage_count ??
                                      0
                                    ).toLocaleString()
                                  : (token.usage_count || 0).toLocaleString()}
                              </span>
                            )}{" "}
                            {showPercentages && (
                              <span>
                                (
                                {token.billing?.tier_usage_percentage != null
                                  ? Number(
                                      token.billing.tier_usage_percentage,
                                    ).toFixed(1)
                                  : usagePercentage.toFixed(1)}
                                %)
                              </span>
                            )}
                          </div>
                          <div className="relative h-1 w-[135px] overflow-hidden rounded text-xs">
                            {/* Background layer with opacity */}
                            <div
                              className={`absolute inset-0 ${getUsageColor(isPremium, monitoringType)} opacity-30`}
                            />
                            {/* Progress bar - full opacity */}
                            <div
                              className={`relative z-10 h-1 rounded ${getUsageColor(isPremium, monitoringType)}`}
                              style={{
                                width: `${token.billing?.tier_usage_percentage != null ? token.billing.tier_usage_percentage : usagePercentage}%`,
                              }}
                            />
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span
                            className={`h-2 w-2 flex-shrink-0 rounded-full ${
                              token.active ? "bg-green-500" : "bg-red-500"
                            }`}
                          />
                          <span
                            className={`text-xs leading-4 font-medium -tracking-wide ${
                              token.active ? "text-green-500" : "text-red-500"
                            }`}
                          >
                            {token.active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex gap-3">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                className="bg-destructive hover:bg-destructive gap-2 rounded-lg p-1.5 text-xs font-medium text-white hover:text-white"
                                variant="minimal"
                                icon
                                onClick={() =>
                                  handleDeleteToken(token.api_key_id)
                                }
                                disabled={
                                  deleteTokenMutation.isPending || !token.active
                                }
                              >
                                {deleteTokenMutation.isPending ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <>
                                    <i className="ri-close-circle-line text-md font-light"></i>
                                    Deactivate
                                  </>
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top" sideOffset={4}>
                              <div className="text-xs">
                                {token.active
                                  ? `Deactivate token: ${isPremium ? "Premium" : tokenType}`
                                  : "Token already inactive"}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination && pagination.total_pages > 1 && (
          <Pagination
            currentPage={pagination.current_page}
            totalPages={pagination.total_pages}
            pageSize={pagination.items_per_page}
            totalRecords={pagination.total_items}
            hasNext={pagination.current_page < pagination.total_pages}
            hasPrevious={pagination.current_page > 1}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            isLightMode={true}
          />
        )}
      </div>
    </div>
  );
}
